<template>
  <div
    v-click-outside="closeMenu"
    class="user-icon-wrap"
  >
    <!-- Icon button — always a dropdown toggle -->
    <ekitabu-button
      v-if="!$auth.loading"
      class="nav-icon-btn"
      :class="{ active: menuOpen }"
      text
      :min-width="0"
      :height="36"
      :ripple="false"
      :title="$auth.isAuthenticated ? $t('menu.profile') : $t('menu.login')"
      @click="toggleMenu"
    >
      <span
        v-if="$auth.isAuthenticated"
        class="user-avatar"
      >{{ userInitial }}</span>
      <user-icon
        v-else
        :size="16"
      />
      <span class="account-label d-none d-md-inline">{{ $t('menu.account') }}</span>
    </ekitabu-button>

    <!-- Dropdown menu -->
    <transition name="dropdown">
      <div
        v-if="menuOpen"
        class="user-dropdown"
      >
        <!-- ══ Authenticated ══ -->
        <template v-if="$auth.isAuthenticated">
          <div class="dropdown-header">
            <span class="dropdown-avatar">{{ userInitial }}</span>
            <div class="dropdown-user-info">
              <span class="dropdown-name">{{ userName }}</span>
              <span class="dropdown-email">{{ userEmail }}</span>
            </div>
          </div>
          <div class="dropdown-divider" />
          <router-link
            :to="{ name: 'userProfile' }"
            class="dropdown-item"
            @click.native="closeMenu"
          >
            <user-icon :size="14" />
            {{ $t('menu.profile') }}
          </router-link>
          <router-link
            v-if="$auth.user && $auth.user.email_verified"
            :to="{ name: 'bookshelf' }"
            class="dropdown-item"
            @click.native="closeMenu"
          >
            <bookshelf-icon
              :size="14"
              color="currentColor"
            />
            {{ $t('menu.bookshelf') }}
          </router-link>
          <router-link
            v-if="isAdmin"
            :to="{ name: 'adminDashboard' }"
            class="dropdown-item"
            @click.native="closeMenu"
          >
            <admin-icon :size="14" />
            {{ $t('menu.admin') }}
          </router-link>
          <div class="dropdown-divider" />
          <ekitabu-button
            class="dropdown-item dropdown-logout"
            text
            block
            :ripple="false"
            :min-width="0"
            @click="logout"
          >
            <logout-icon :size="14" />
            {{ $t('menu.logout') }}
          </ekitabu-button>
        </template>

        <!-- ══ Unauthenticated ══ -->
        <template v-else>
          <div class="dropdown-guest-header">
            <span class="dropdown-guest-icon">
              <user-icon :size="20" />
            </span>
            <span class="dropdown-guest-text">{{ $t('menu.welcomeGuest') }}</span>
          </div>
          <div class="dropdown-divider" />
          <ekitabu-button
            class="dropdown-item"
            text
            block
            :ripple="false"
            :min-width="0"
            @click="login"
          >
            <login-icon :size="14" />
            {{ $t('menu.login') }}
          </ekitabu-button>
          <ekitabu-button
            class="dropdown-item dropdown-signup"
            text
            block
            :ripple="false"
            :min-width="0"
            @click="signUp"
          >
            <register-icon :size="14" />
            {{ $t('menu.signUp') }}
          </ekitabu-button>
        </template>
      </div>
    </transition>
  </div>
</template>

<script>
import AdminCheckMixin from "@/mixins/AdminCheckMixin";
import UserIcon from "@/components/icons/UserIcon.vue";
import LoginIcon from "@/components/icons/LoginIcon.vue";
import LogoutIcon from "@/components/icons/LogoutIcon.vue";
import RegisterIcon from "@/components/icons/RegisterIcon.vue";
import AdminIcon from "@/components/icons/AdminIcon.vue";
import BookshelfIcon from "@/components/icons/BookshelfIcon.vue";

export default {
  name: "UserIconMenu",
  components: {
    UserIcon,
    LoginIcon,
    LogoutIcon,
    RegisterIcon,
    AdminIcon,
    BookshelfIcon,
  },
  mixins: [AdminCheckMixin],
  data() {
    return {
      menuOpen: false,
    };
  },
  computed: {
    userInitial() {
      if (this.$auth.user && this.$auth.user.name) {
        return this.$auth.user.name.charAt(0).toUpperCase();
      }
      if (this.$auth.user && this.$auth.user.email) {
        return this.$auth.user.email.charAt(0).toUpperCase();
      }
      return "?";
    },
    userName() {
      return this.$auth.user?.name || "";
    },
    userEmail() {
      return this.$auth.user?.email || "";
    },
  },
  methods: {
    toggleMenu() {
      this.menuOpen = !this.menuOpen;
    },
    closeMenu() {
      this.menuOpen = false;
    },
    async login() {
      this.closeMenu();
      await this.$auth.loginWithRedirect({
        appState: {
          targetUrl: this.$route.fullPath,
        },
      });
    },
    async signUp() {
      this.closeMenu();
      await this.$auth.loginWithRedirect({
        screen_hint: 'signup',
        appState: {
          targetUrl: '/verification-pending',
        },
      });
    },
    async logout() {
      this.closeMenu();
      await this.$auth.logout({
        returnTo: window.location.origin,
      });
    },
  },
};
</script>

<style scoped lang="scss">
.user-icon-wrap {
  position: relative;
}

.nav-icon-btn {
  width: 36px;
  height: 36px;
  border-radius: 6px;
  border: 1px solid $book-grey-lighter;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: $book-ink;
  transition: background 0.15s, border-color 0.15s;
  padding: 0;

  &:hover {
    background: $book-grey-lightest;
  }

  &.active {
    border-color: var(--v-primary-base);
    background: rgba($book-red, 0.04);
  }
}

@media #{map-get($display-breakpoints, 'md-and-up')} {
  .nav-icon-btn {
    width: auto;
    padding: 0 10px;
    gap: 6px;
  }
}

.account-label {
  font-size: 0.8125rem;
  font-weight: 600;
  white-space: nowrap;
  color: $book-ink;
}

.user-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: $book-brand-gradient;
  color: white;
  font-size: 0.6875rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.user-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 240px;
  background: white;
  border: 1px solid $book-grey-lighter;
  border-radius: 10px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.06);
  z-index: $z-overlay;
  overflow: hidden;
}

/* ── Authenticated header ── */
.dropdown-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
}

.dropdown-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: $book-brand-gradient;
  color: white;
  font-size: 0.8125rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.dropdown-user-info {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.dropdown-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dropdown-name {
  @extend .dropdown-truncate;
  font-size: 0.8125rem;
  font-weight: 700;
  color: $book-ink;
}

.dropdown-email {
  @extend .dropdown-truncate;
  font-size: 0.6875rem;
  color: $book-grey;
}

/* ── Guest header ── */
.dropdown-guest-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
}

.dropdown-guest-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: $book-grey-lightest;
  color: $book-grey;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.dropdown-guest-text {
  font-size: 0.8125rem;
  font-weight: 600;
  color: $book-grey;
}

.dropdown-divider {
  height: 1px;
  background: $book-grey-lighter;
}

/* ── Menu items ── */
.dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  font-size: 0.8125rem;
  font-weight: 600;
  color: $book-ink;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.12s;
  width: 100%;
  border: none;
  background: none;
  text-align: left;

  svg {
    color: $book-grey;
    flex-shrink: 0;
  }

  &:hover {
    background: $book-grey-lightest;
  }

  &.router-link-exact-active {
    color: var(--v-primary-base);
    svg { color: var(--v-primary-base); }
  }
}

::v-deep .dropdown-item .v-btn__content {
  justify-content: flex-start;
}

.dropdown-signup {
  color: var(--v-primary-base);
  font-weight: 700;
  svg { color: var(--v-primary-base); }
}

.dropdown-logout {
  color: var(--v-primary-base);
  svg { color: var(--v-primary-base); }

  &:hover {
    background: rgba($book-red, 0.04);
  }
}

// Dropdown animation
.dropdown-enter-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.dropdown-leave-active {
  transition: opacity 0.1s ease, transform 0.1s ease;
}
.dropdown-enter,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
